import React, { useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import CommentCard from './comments/CommentCard';
import CommentCreatingCard from './comments/CommentCreatingCard';
import SuggestionCard from './suggestions/SuggestionCard';
import SuggestionCreatingCard from './suggestions/SuggestionCreatingCard';
import { useCardHeights } from '../hooks/useCardHeights';

const CARD_GAP = 8; // px between adjacent cards
const DEFAULT_CARD_HEIGHT = 48; // collapsed card height fallback

/**
 * Google Docs positioning algorithm.
 *
 * Rules:
 * 1. Each card's "ideal" position is the Y offset of its text anchor.
 * 2. The active card is placed at its exact ideal position (never shifted).
 * 3. Cards above the active card are pushed upward to avoid overlap.
 * 4. Cards below the active card are pushed downward to avoid overlap.
 * 5. When no card is active, cards pack tightly downward from their ideal position.
 * 6. When two cards are less than 40px apart in ideal position, they are considered
 *    "at the same location" and sorted newest-first (smallest createdAt goes up).
 *
 * @param {Array}  allCards      — Array of { id, createdAt, type }
 * @param {Object} idealPositions — { [id]: topPx }  (from useCardPositions)
 * @param {string|null} activeId
 * @param {Object} cardHeights  — { [id]: px }  (from useCardHeights)
 * @returns {Object} { [id]: topPx }
 */
function resolvePositions(allCards, idealPositions, activeId, cardHeights) {
  const withIdeal = allCards
    .filter(c => idealPositions[c.id] !== undefined)
    .map(c => ({
      id: c.id,
      ideal: idealPositions[c.id],
      createdAt: c.createdAt || c.timestamp || 0,
    }))
    .sort((a, b) => {
      // Close together (< 40px) → sort newest first (higher up)
      if (Math.abs(a.ideal - b.ideal) < 40) {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
      return a.ideal - b.ideal;
    });

  if (!withIdeal.length) return {};

  const result = {};
  const h = (id) => cardHeights[id] || DEFAULT_CARD_HEIGHT;
  const activeIdx = withIdeal.findIndex(c => c.id === activeId);

  if (activeIdx === -1) {
    // No active card — pack tightly downward
    let cursor = 0;
    for (const card of withIdeal) {
      const top = Math.max(card.ideal, cursor);
      result[card.id] = top;
      cursor = top + h(card.id) + CARD_GAP;
    }
    return result;
  }

  // Active card at its exact ideal position
  const activeCard = withIdeal[activeIdx];
  result[activeCard.id] = activeCard.ideal;

  // Cards ABOVE active — push upward so they don't overlap active
  let ceiling = activeCard.ideal;
  for (let i = activeIdx - 1; i >= 0; i--) {
    const card = withIdeal[i];
    const cardH = h(card.id);
    // Prefer ideal; pull it up if it would overlap the card below
    const top = Math.min(card.ideal, ceiling - cardH - CARD_GAP);
    result[card.id] = Math.max(0, top);
    ceiling = result[card.id];
  }

  // Cards BELOW active — push downward so they don't overlap active
  let floor = activeCard.ideal + h(activeCard.id) + CARD_GAP;
  for (let i = activeIdx + 1; i < withIdeal.length; i++) {
    const card = withIdeal[i];
    const top = Math.max(card.ideal, floor);
    result[card.id] = top;
    floor = top + h(card.id) + CARD_GAP;
  }

  return result;
}

/**
 * CardsColumn — unified right-margin column that positions and renders
 * ALL comment and suggestion cards together (Google Docs style).
 *
 * Handles both comments and suggestions in one coordinate space so they never overlap.
 */
const CardsColumn = ({
  // Comments
  comments = [],
  pendingComment,
  commentPositions = {},
  activeCommentId,
  onSetActiveComment,
  commentHandlers = {},
  // Suggestions
  suggestions = [],
  pendingSuggestion,
  suggestionPositions = {},
  activeSuggestionId,
  onSetActiveSuggestion,
  suggestionHandlers = {},
  // Shared
  currentUser,
}) => {
  const { registerRef, heights } = useCardHeights();

  // Stable ref callbacks per id — prevents React from re-calling the ref on every
  // render (which would cause an infinite loop via useCardHeights → setHeights).
  // Cached by id; cleaned up automatically when the element unmounts (ref called with null).
  const refCacheRef = useRef({});
  const getStableRef = (id) => {
    if (!refCacheRef.current[id]) {
      refCacheRef.current[id] = (el) => {
        if (!el) delete refCacheRef.current[id];
        registerRef(id, el);
      };
    }
    return refCacheRef.current[id];
  };

  const {
    addComment,
    resolveComment,
    deleteComment,
    updateComment,
    replyToComment,
    deleteReply,
    setPendingComment,
  } = commentHandlers;

  const {
    addSuggestion,
    acceptSuggestion,
    rejectSuggestion,
    setPendingSuggestion,
  } = suggestionHandlers;

  // Merge all cards for joint position resolution
  const allCards = useMemo(() => {
    const commentCards = comments
      .filter(c => !c.resolved)
      .map(c => ({
        id: c.id,
        type: 'comment',
        createdAt: c.timestamp || c.created_at || 0,
      }));
    const suggestionCards = suggestions.map(s => ({
      id: s.id,
      type: 'suggestion',
      createdAt: s.timestamp || s.created_at || 0,
    }));
    return [...commentCards, ...suggestionCards];
  }, [comments, suggestions]);

  // Merge all ideal positions
  const allIdealPositions = useMemo(() => ({
    ...commentPositions,
    ...suggestionPositions,
  }), [commentPositions, suggestionPositions]);

  // Active id is whichever is non-null (only one type active at a time)
  const activeId = activeCommentId || activeSuggestionId;

  // Compute resolved positions
  const resolvedPositions = useMemo(
    () => resolvePositions(allCards, allIdealPositions, activeId, heights),
    [allCards, allIdealPositions, activeId, heights]
  );

  // Pending card positions (shown above the resolved set; don't participate in joint resolve)
  const pendingCommentTop = pendingComment?.selectionY !== undefined
    ? Math.max(0, pendingComment.selectionY)
    : 16;

  const pendingSuggestionTop = pendingSuggestion?.selectionY !== undefined
    ? Math.max(0, pendingSuggestion.selectionY)
    : 16;

  // Card lookup maps
  const commentMap = useMemo(() => {
    const map = {};
    comments.forEach(c => { map[c.id] = c; });
    return map;
  }, [comments]);

  const suggestionMap = useMemo(() => {
    const map = {};
    suggestions.forEach(s => { map[s.id] = s; });
    return map;
  }, [suggestions]);

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
      }}
    >
      {/* Creating comment card */}
      {pendingComment && (
        <Box
          sx={{
            position: 'absolute',
            top: pendingCommentTop,
            left: 8,
            right: 8,
            zIndex: 20,
            pointerEvents: 'auto',
          }}
          ref={getStableRef('__pending_comment__')}
        >
          <CommentCreatingCard
            currentUser={currentUser}
            selectedText={pendingComment.selectedText}
            onSubmit={(text) => {
              if (addComment) addComment(text, pendingComment);
              if (setPendingComment) setPendingComment(null);
            }}
            onCancel={() => {
              if (setPendingComment) setPendingComment(null);
            }}
          />
        </Box>
      )}

      {/* Creating suggestion card */}
      {pendingSuggestion && (
        <Box
          sx={{
            position: 'absolute',
            top: pendingSuggestionTop,
            left: 8,
            right: 8,
            zIndex: 20,
            pointerEvents: 'auto',
          }}
          ref={getStableRef('__pending_suggestion__')}
        >
          <SuggestionCreatingCard
            currentUser={currentUser}
            originalText={pendingSuggestion.originalText}
            onSubmit={(suggestedText) => {
              if (addSuggestion) addSuggestion(suggestedText, pendingSuggestion);
              if (setPendingSuggestion) setPendingSuggestion(null);
            }}
            onCancel={() => {
              if (setPendingSuggestion) setPendingSuggestion(null);
            }}
          />
        </Box>
      )}

      {/* Resolved comment + suggestion cards — sorted and positioned */}
      {allCards.map(({ id, type }) => {
        const top = resolvedPositions[id];
        // Skip until useCardPositions has computed a real position (prevents flash at top:0)
        if (top === undefined) return null;

        const isActive = id === activeId;

        if (type === 'comment') {
          const comment = commentMap[id];
          if (!comment) return null;
          return (
            <Box
              key={id}
              ref={getStableRef(id)}
              sx={{
                position: 'absolute',
                top,
                left: 8,
                right: 8,
                zIndex: isActive ? 10 : 1,
                pointerEvents: 'auto',
                transition: 'top 180ms cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <CommentCard
                comment={comment}
                currentUser={currentUser}
                isActive={isActive}
                onActivate={() => {
                  if (onSetActiveComment) onSetActiveComment(id);
                  if (onSetActiveSuggestion) onSetActiveSuggestion(null);
                }}
                onResolve={(resolved) => {
                  if (resolveComment) resolveComment(id, resolved);
                }}
                onDelete={() => {
                  if (deleteComment) deleteComment(id);
                  if (onSetActiveComment) onSetActiveComment(null);
                }}
                onEdit={(text) => {
                  if (updateComment) updateComment(id, text);
                }}
                onAddReply={(text) => {
                  if (replyToComment) replyToComment(id, text);
                }}
                onDeleteReply={(replyId) => {
                  if (deleteReply) deleteReply(id, replyId);
                }}
              />
            </Box>
          );
        }

        if (type === 'suggestion') {
          const suggestion = suggestionMap[id];
          if (!suggestion) return null;
          return (
            <Box
              key={id}
              ref={getStableRef(id)}
              sx={{
                position: 'absolute',
                top,
                left: 8,
                right: 8,
                zIndex: isActive ? 10 : 1,
                pointerEvents: 'auto',
                transition: 'top 180ms cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <SuggestionCard
                suggestion={suggestion}
                isActive={isActive}
                onActivate={() => {
                  if (onSetActiveSuggestion) onSetActiveSuggestion(id);
                  if (onSetActiveComment) onSetActiveComment(null);
                }}
                onAccept={() => {
                  if (acceptSuggestion) acceptSuggestion(id);
                  if (onSetActiveSuggestion) onSetActiveSuggestion(null);
                }}
                onReject={() => {
                  if (rejectSuggestion) rejectSuggestion(id);
                  if (onSetActiveSuggestion) onSetActiveSuggestion(null);
                }}
              />
            </Box>
          );
        }

        return null;
      })}
    </Box>
  );
};

export default CardsColumn;
